import { cleanQueryValue } from "./cleanQueryValue";
import {
  addMonthsSameDay,
  clampToFlightBookingWindow,
  getDefaultFlightDepartureDate,
} from "../utils/booking/flightBookingWindow";

export interface FlightData {
  departureAddress: string;
  arrivalCityAddress: string;
  arrivalAddress: string;
  arrival: string;
  departure: string;
  departureDate: string;
  returnDate: string;
  departureAirportFullName: string;
  arrivalAirportFullName: string;
  noOfDays: number;
}

export interface StayData {
  checkIn: string;
  checkOut: string;
  country: string;
  city: string;
  latitude: string;
  longitude: string;
  locality: string;
  accommodation_location_name: string;
  isSelected: boolean;
  selectedLocation: string;
}

const normalizeUrl = (url: string): string => (url.includes("?") ? url : url.replace("&", "?"));

const cleanAirportName = (name?: string | null, city?: string | null): string => {
  if (!name || !city) return "";
  return name.replace(new RegExp(`\\s*${city}$`, "i"), "").trim();
};

const formatDateLocal = (d: Date): string => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const addOneMonthSameDay = (date: Date): Date => addMonthsSameDay(date, 1);
const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};
const parseDate = (dateStr?: string): Date | null => {
  if (!dateStr) return null;

  const parts = dateStr.split("-").map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return null;

  let year: number;
  let month: number;
  let day: number;

  // YYYY-XX-XX
  if (dateStr.startsWith(parts[0].toString()) && parts[0] > 31) {
    year = parts[0];

    // YYYY-MM-DD (ISO)
    if (parts[1] <= 12) {
      month = parts[1];
      day = parts[2];
    }
    // YYYY-DD-MM
    else {
      day = parts[1];
      month = parts[2];
    }
  }
  // XX-XX-YYYY
  else if (parts[2] > 31) {
    year = parts[2];

    // MM-DD-YYYY
    if (parts[0] <= 12) {
      month = parts[0];
      day = parts[1];
    }
    // DD-MM-YYYY
    else {
      day = parts[0];
      month = parts[1];
    }
  } else {
    return null;
  }

  const date = new Date(year, month - 1, day);

  // Final validity check (prevents overflow like Feb 30)
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }

  return date;
};

export const extractFlightDataFromUrl = (currentUrl: string): FlightData[] => {
  // Ensure URL is valid for parsing
  const fixedUrl = currentUrl.includes("?") ? currentUrl : currentUrl.replace("&", "?");

  const urlObj = new URL(fixedUrl);
  const params = urlObj.searchParams;

  // Extract & sanitize params
  const departureCity = cleanQueryValue(params.get("departure"));
  const departureAirportCode = cleanQueryValue(params.get("departureAirportCode"));
  const departureAirportName = cleanQueryValue(params.get("departureAirportName"));

  const arrivalCity = cleanQueryValue(params.get("arrival"));
  const arrivalAirportCode = cleanQueryValue(params.get("arrivalAirportCode"));
  const arrivalAirportName = cleanQueryValue(params.get("arrivalAirportName"));

  const urlDepartureDate = cleanQueryValue(params.get("departureDate"));
  const urlArrivalDate = cleanQueryValue(params.get("arrivalDate"));

  // Base date logic
  const today = new Date();
  const checkDate = new Date(today);
  checkDate.setMonth(today.getMonth() + 1);

  // --- Departure date ---
  // Default departure = the far edge of the bookable window (today + 11 months, less the
  // cross-timezone safety day — see flightBookingWindow). A URL-supplied date is clamped to
  // that same edge: deep-link dates never pass through a date picker, so without the clamp a
  // far-future campaign link reaches the engine and comes back as a hard 400.
  let departureDate = parseDate(urlDepartureDate);
  if (!departureDate || departureDate < today) {
    departureDate = getDefaultFlightDepartureDate();
  } else {
    departureDate = clampToFlightBookingWindow(departureDate);
  }

  // --- Return date ---
  // Both legs are capped at the same edge and the departure default already sits on it, so the
  // return default is the departure day itself (user shortens departure for a longer trip). An
  // explicit return date from the URL (on/after departure) is kept, clamped the same way.
  let arrivalDate = parseDate(urlArrivalDate);
  if (!arrivalDate || arrivalDate < departureDate) {
    arrivalDate = new Date(departureDate);
  } else {
    arrivalDate = clampToFlightBookingWindow(arrivalDate);
  }

  return [
    {
      departureAddress: departureCity || "",
      arrivalCityAddress: arrivalCity || "",
      arrivalAddress: arrivalCity || "",
      arrival: arrivalAirportCode || "",
      departure: departureAirportCode || "",
      departureDate: formatDateLocal(departureDate) ?? "",
      returnDate: formatDateLocal(arrivalDate) ?? "",
      departureAirportFullName: cleanAirportName(departureAirportName, departureCity),
      arrivalAirportFullName: cleanAirportName(arrivalAirportName, arrivalCity),
      noOfDays: 1,
    },
    {
      departureAddress: arrivalCity || "",
      arrivalCityAddress: departureCity || "",
      arrivalAddress: departureCity || "",
      arrival: departureAirportCode || "",
      departure: arrivalAirportCode || "",
      departureDate: formatDateLocal(arrivalDate) ?? "",
      returnDate: formatDateLocal(arrivalDate) ?? "",
      departureAirportFullName: cleanAirportName(arrivalAirportName, arrivalCity),
      arrivalAirportFullName: cleanAirportName(departureAirportName, departureCity),
      noOfDays: 5,
    },
  ];
};

export const extractStayDataFromUrl = (currentUrl: string): StayData[] => {
  const urlObj = new URL(normalizeUrl(currentUrl));
  const params = urlObj.searchParams;

  const stayCity = cleanQueryValue(params.get("stayCity"));
  const countryCode = cleanQueryValue(params.get("countryCode"));
  const urlCheckIn = cleanQueryValue(params.get("checkin"));
  const urlCheckOut = cleanQueryValue(params.get("checkout"));

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let checkInDate = parseDate(urlCheckIn);
  if (!checkInDate || checkInDate < today) {
    checkInDate = addOneMonthSameDay(today);
  }

  let checkOutDate = parseDate(urlCheckOut);
  if (!checkOutDate || checkOutDate <= checkInDate) {
    checkOutDate = addDays(checkInDate, 5);
  }

  return [
    {
      checkIn: formatDateLocal(checkInDate),
      checkOut: formatDateLocal(checkOutDate),
      country: countryCode || "",
      city: stayCity || "",
      latitude: "",
      longitude: "",
      locality: stayCity || "",
      accommodation_location_name: stayCity || "",
      isSelected: true,
      selectedLocation: stayCity || "",
    },
  ];
};
